import { logAmplitudeEvent } from 'amplitude/amplitude'
import { DataProviderProxy } from 'ra-core'
import { Dispatch } from 'redux'
import { isActionWhitelisted } from '../actionPermissions'
import { AdaAction } from '../actions'
import { resolveSystemTemplatesWithDataProvider } from '../utils/resolveSystemTemplates'
import { findTargetMessageIndex, getLastMessageIndex } from './helpers'

function setAutoDesignRunning(dispatch: Dispatch, projectId: number | undefined, isAutoAccepted: boolean) {
  const lastMessageIndex = getLastMessageIndex()
  const toolExecutionState = {
    toolType: 'autoDesign' as const,
    status: 'running' as const,
    projectId,
    isAutoAccepted,
  }

  if (lastMessageIndex >= 0) {
    dispatch({
      type: 'UPDATE_TOOL_EXECUTION_STATE',
      messageIndex: lastMessageIndex,
      toolExecutionState,
    })
  } else {
    // Fallback: create a new message if there are no messages (shouldn't happen normally)
    dispatch({
      type: 'ADD_MESSAGE',
      content: 'Running auto-design...',
      direction: 'receive',
      origin: 'app',
      sharedWithContexts: ['text'],
      toolExecutionState,
    })
  }
}

function getFacetsGeoJson(): any {
  const spatialFacets = window.editor?.enumerateFacets?.({ includeNonSpatialFacets: false }) || []
  const hasSpatialFacets = spatialFacets.length > 0 && spatialFacets.some((f: any) => f.vertices?.length > 0)
  return hasSpatialFacets ? window.GeoJSONHelper?.toGeoJSON?.(window.editor) : null
}

function extractAutoDesignErrorMessage(autoDesignResult: any): string {
  const errorMessages = autoDesignResult?.messages?.filter((m: any) => m.type === 'error') || []
  const failureReasons =
    autoDesignResult?.template_results
      ?.filter((t: any) => t.status === 'failed')
      ?.map((t: any) => t.failure_reason || t.template_name + ' failed') || []

  return (
    errorMessages.map((m: any) => m.message).join('; ') ||
    failureReasons.join('; ') ||
    autoDesignResult?.system_error ||
    'Auto-design failed - no systems were generated'
  )
}

function buildSystemDescription(designData: any): string {
  const systems = designData?.object?.children?.filter((child: any) => child.type === 'OsSystem') || []
  const descriptions: string[] = []

  systems.forEach((system: any) => {
    const kwStc = system.userData?.kw_stc || 0
    const batteryKwh = system.userData?.battery_total_kwh || 0
    let systemDesc = `${kwStc} kW PV`
    if (batteryKwh > 0) {
      systemDesc += `, ${batteryKwh} kWh Battery`
    }
    descriptions.push(systemDesc)
  })

  return descriptions.join(' ')
}

function updateToolExecutionState(dispatch: Dispatch, projectId: number | undefined, toolExecutionState: any) {
  const targetMessageIndex = findTargetMessageIndex()
  if (targetMessageIndex >= 0) {
    dispatch({
      type: 'UPDATE_TOOL_EXECUTION_STATE',
      messageIndex: targetMessageIndex,
      toolExecutionState: {
        ...toolExecutionState,
        projectId,
      },
    })
  }
}

function handleAutoDesignFailure(
  dispatch: Dispatch,
  projectId: number | undefined,
  autoDesignResult: any,
  overallStatus: string
) {
  const errorMessage = extractAutoDesignErrorMessage(autoDesignResult)

  if (window.debugAdaActions) {
    console.error('[handleAutoDesignEphemeral] Auto-design failed:', {
      overallStatus,
      errorMessage,
    })
  }

  updateToolExecutionState(dispatch, projectId, {
    toolType: 'autoDesign',
    status: 'error',
    description: errorMessage,
    error: {
      message: errorMessage,
      errorCode: 'AUTO_DESIGN_FAILED',
      errorDetails: {
        overallStatus,
        successfulTemplateCount: autoDesignResult?.successful_template_count || 0,
        failedTemplateCount: autoDesignResult?.failed_template_count || 0,
        templateResults: autoDesignResult?.template_results || [],
      },
    },
  })
}

function handleAutoDesignSuccess(
  dispatch: Dispatch,
  projectId: number | undefined,
  designData: any,
  autoDesignResult: any
) {
  if (window.debugAdaActions) {
    console.log('[handleAutoDesignEphemeral] Processing design data:', {
      hasDesignData: !!designData,
      hasObject: !!designData?.object,
      childrenCount: designData?.object?.children?.length || 0,
      systemsCount: designData?.object?.children?.filter((c: any) => c.type === 'OsSystem')?.length || 0,
    })
  }

  const description = buildSystemDescription(designData)

  if (window.debugAdaActions) {
    console.log('[handleAutoDesignEphemeral] Storing designData in toolExecutionState')
  }

  updateToolExecutionState(dispatch, projectId, {
    toolType: 'autoDesign',
    status: 'ready',
    description: description || 'Auto-design complete',
    data: { designData, autoDesignResult },
    designData,
  })

  if (window.debugAdaActions) {
    console.log('Updated auto design message to ready state')
  }
}

function handleWorkerError(dispatch: Dispatch, projectId: number | undefined, error: any) {
  console.error('Error running auto design:', error)
  if (error?.cause) {
    console.error('Error cause:', error.cause)
  }

  const errorData = error?.cause || error
  const errorMessage = errorData?.message || errorData?.detail || error?.message || 'Auto-design failed'
  const errorCode = errorData?.['error-code'] || errorData?.errorCode
  const errorDetails = errorData?.['error-details'] || errorData?.errorDetails

  updateToolExecutionState(dispatch, projectId, {
    toolType: 'autoDesign',
    status: 'error',
    description: errorMessage,
    error: {
      message: errorMessage,
      errorCode,
      errorDetails,
      httpCode: errorData?.http_code,
    },
  })
}

function processAutoDesignResponse(dispatch: Dispatch, projectId: number | undefined, response: any) {
  if (window.debugAdaActions) {
    console.log('Auto design worker completed, response:', response)
  }

  if (!projectId || !dispatch) {
    console.warn('Auto design response missing required context', {
      hasResponse: !!response,
      projectId,
      hasDispatch: !!dispatch,
    })
    return
  }

  const designData = response?.designData
  const autoDesignResult = response?.autoDesignResult

  if (window.debugAdaActions) {
    console.log('[handleAutoDesignEphemeral] Processing response:', {
      hasDesignData: !!designData,
      hasAutoDesignResult: !!autoDesignResult,
      overallStatus: autoDesignResult?.overall_status,
      successfulTemplateCount: autoDesignResult?.successful_template_count || 0,
      failedTemplateCount: autoDesignResult?.failed_template_count || 0,
      totalTemplateCount: autoDesignResult?.total_template_count || 0,
    })
  }

  const overallStatus = autoDesignResult?.overall_status
  const hasSystems = (designData?.object?.children?.filter((c: any) => c.type === 'OsSystem')?.length || 0) > 0

  if (overallStatus === 'failed' || !hasSystems) {
    handleAutoDesignFailure(dispatch, projectId, autoDesignResult, overallStatus)
    return
  }

  handleAutoDesignSuccess(dispatch, projectId, designData, autoDesignResult)
}

export async function handleAutoDesignEphemeral(
  action: AdaAction,
  orgId: number | undefined,
  projectId: number | undefined,
  dataProvider: DataProviderProxy,
  dispatch: Dispatch
) {
  try {
    const systemTemplatesJson = await resolveSystemTemplatesWithDataProvider(action.args, orgId, dataProvider)
    if (!systemTemplatesJson || systemTemplatesJson.length === 0) {
      console.warn('No templates resolved for autoDesignEphemeral')
      return
    }

    if (window.debugAdaActions) {
      console.log('Starting autoDesignEphemeral with templates:', systemTemplatesJson.length)
    }

    logAmplitudeEvent('auto_design_generate_systems_clicked', {
      source: 'ada_chat_action',
      templateCount: systemTemplatesJson.length,
      orgId: orgId,
    })

    const isAutoAccepted = isActionWhitelisted('autoDesignEphemeral')
    setAutoDesignRunning(dispatch, projectId, isAutoAccepted)

    const facetsGeoJson = getFacetsGeoJson()

    // Call the worker directly so we can get the response, store it, send message, then load
    window.AutoDesignHelper.autoDesignRunAndLoadWorker(
      null,
      systemTemplatesJson,
      null,
      facetsGeoJson,
      null,
      {},
      false,
      null,
      null,
      null,
      false,
      false, // showNotifications
      {}
    )
      .then((response: any) => processAutoDesignResponse(dispatch, projectId, response))
      .catch((error: any) => handleWorkerError(dispatch, projectId, error))
  } catch (error) {
    console.error('Error resolving templates or running auto design:', error)
  }
}
